import type { AxiosInstance } from 'axios';

export type PosTerminalStatus = 'ACTIVE' | 'RETIRED';
export type PosShiftStatus = 'OPEN' | 'CLOSED' | 'RECONCILED';
export type PosSaleStatus = 'DRAFT' | 'COMPLETED' | 'PARTIALLY_REFUNDED' | 'REFUNDED' | 'VOIDED';
export type PosPaymentMethod = 'CASH' | 'UPI' | 'QR' | 'CARD' | 'WALLET';

export interface PosBranch {
  id: string;
  code: string;
  name: string;
  warehouseId: string;
  gstin: string | null;
  stateCode: string;
  invoicePrefix: string;
  invoiceSequence: number;
  active: boolean;
  createdAt: string;
}

export interface PosTerminal {
  id: string;
  branchId: string;
  code: string;
  label: string;
  status: PosTerminalStatus;
  createdAt: string;
}

export interface PosShift {
  id: string;
  shiftNumber: string;
  branchId: string;
  terminalId: string;
  cashierId: string;
  status: PosShiftStatus;
  openedAt: string;
  closedAt: string | null;
  openingFloatPaise: number;
  closingCashCountedPaise: number | null;
  expectedCashPaise: number | null;
  variancePaise: number | null;
  notes: string | null;
}

export interface ShiftSummary {
  shiftId: string;
  status: PosShiftStatus;
  saleCount: number;
  refundCount: number;
  grossSalesPaise: number;
  refundsPaise: number;
  netSalesPaise: number;
  cashSalesPaise: number;
  cashRefundsPaise: number;
  upiSalesPaise: number;
  cardSalesPaise: number;
  walletSalesPaise: number;
  expectedCashPaise: number;
  countedCashPaise: number | null;
  variancePaise: number | null;
}

export interface PosSaleItem {
  id: string;
  itemKey: string;
  description: string;
  sku: string | null;
  hsnSac: string | null;
  qty: number;
  unitPricePaise: number;
  gstRate: number;
  taxPaise: number;
  discountPaise: number;
  lineSubtotalPaise: number;
  refundedQty: number;
  cogsPaise: number;
}

export interface PosPayment {
  id: string;
  method: PosPaymentMethod;
  amountPaise: number;
  reference: string | null;
  note: string | null;
  createdAt: string;
}

export interface PosSale {
  id: string;
  invoiceNumber: string;
  branchId: string;
  shiftId: string;
  cashierId: string;
  customerId: string | null;
  status: PosSaleStatus;
  itemsSubtotalPaise: number;
  taxPaise: number;
  discountPaise: number;
  grandTotalPaise: number;
  refundedPaise: number;
  cogsPaise: number;
  intraState: boolean;
  customerName: string | null;
  customerPhone: string | null;
  customerGstin: string | null;
  notes: string | null;
  createdAt: string;
  completedAt: string | null;
  refundedAt: string | null;
  voidedAt: string | null;
}

export interface PosSaleDetail extends PosSale {
  items: PosSaleItem[];
  payments: PosPayment[];
}

export interface CreateSaleRequest {
  shiftId: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerGstin?: string;
  buyerStateCode?: string;
  items: {
    itemKey: string;
    description: string;
    sku?: string;
    hsnSac?: string;
    qty: number;
    unitPricePaise: number;
    gstRate?: number;
    discountPaise?: number;
  }[];
  payments: { method: PosPaymentMethod; amountPaise: number; reference?: string; note?: string }[];
  notes?: string;
}

export function posEndpoints(http: AxiosInstance) {
  return {
    // Branches
    listBranches: () =>
      http.get<{ data: PosBranch[] }>('/admin/pos/branches').then((r) => r.data.data),
    createBranch: (body: {
      code: string;
      name: string;
      warehouseId: string;
      gstin?: string;
      stateCode: string;
      invoicePrefix?: string;
      pincode?: string;
    }) => http.post<PosBranch>('/admin/pos/branches', body).then((r) => r.data),

    // Terminals
    listTerminals: (branchId: string) =>
      http
        .get<{ data: PosTerminal[] }>(`/admin/pos/branches/${branchId}/terminals`)
        .then((r) => r.data.data),
    createTerminal: (body: { branchId: string; code: string; label: string }) =>
      http.post<PosTerminal>('/admin/pos/terminals', body).then((r) => r.data),
    retireTerminal: (terminalId: string) =>
      http.post<PosTerminal>(`/admin/pos/terminals/${terminalId}/retire`).then((r) => r.data),

    // Shifts — cashier
    openShift: (body: { terminalId: string; openingFloatPaise?: number; notes?: string }) =>
      http.post<PosShift>('/pos/shifts', body).then((r) => r.data),
    closeShift: (shiftId: string, body: { closingCashCountedPaise: number; notes?: string }) =>
      http.post<PosShift>(`/pos/shifts/${shiftId}/close`, body).then((r) => r.data),
    shiftSummary: (shiftId: string) =>
      http.get<ShiftSummary>(`/pos/shifts/${shiftId}/summary`).then((r) => r.data),
    myShifts: () => http.get<{ data: PosShift[] }>('/pos/shifts/me').then((r) => r.data.data),

    // Shifts — admin
    listShifts: () => http.get<{ data: PosShift[] }>('/admin/pos/shifts').then((r) => r.data.data),

    // Sales — cashier
    createSale: (body: CreateSaleRequest) =>
      http.post<PosSaleDetail>('/pos/sales', body).then((r) => r.data),
    mySales: () => http.get<{ data: PosSale[] }>('/pos/sales/me').then((r) => r.data.data),
    getSale: (saleId: string) =>
      http.get<PosSaleDetail>(`/pos/sales/${saleId}`).then((r) => r.data),
    getSaleInvoice: (saleId: string) =>
      http.get(`/pos/sales/${saleId}/invoice`).then((r) => r.data),

    // Sales — admin
    refundSale: (
      saleId: string,
      body: {
        lines: { saleItemId: string; qty: number }[];
        method?: PosPaymentMethod;
        reason: string;
        restock?: boolean;
      },
    ) => http.post(`/admin/pos/sales/${saleId}/refund`, body).then((r) => r.data),
    branchSales: (branchId: string) =>
      http
        .get<{ data: PosSale[] }>(`/admin/pos/branches/${branchId}/sales`)
        .then((r) => r.data.data),
  };
}

export type PosEndpoints = ReturnType<typeof posEndpoints>;
